import { useI18n } from 'Hooks';

import { getLangText } from '../../utils/langText';

export const getHeaderText = (contentType, modalTitle, t) => {
  let title = '';
  let desc = '';

  switch (contentType) {
    case 'articles':
      title = `${t('PIM.Article')} / ${modalTitle}`;
      desc = t('PIM.ArticleHeaderDescription');
      break;

    case 'categories':
      title = `${t('PIM.Category')} / ${modalTitle}`;
      desc = t('PIM.CategoryHeaderDescription');
      break;

    case 'menus':
      title = `${t('PIM.Menu')} / ${modalTitle}`;
      desc = t('PIM.MenuHeaderDescription');
      break;

    case 'upsell':
      title = `${t('PIM.Upsell')} / ${modalTitle}`;
      desc = t('PIM.UpsellHeaderDescription');
      break;

    case 'ingredients':
      title = modalTitle;
      desc = t('PIM.IngredientHeaderDescription');
      break;

    case 'courses':
      title = `${t('PIM.CourseType')} / ${modalTitle}`;
      desc = t('PIM.CourseHeaderDescription');
      break;

    case 'messages':
      title = `${t('PIM.Message')} / ${modalTitle}`;
      desc = t('PIM.MessageHeaderDescription');
      break;

    default:
      break;
  }

  return { title, desc };
};

export const getPimCreateText = (t) => ({
  articles: {
    title: t('PIM.Article'),
    description: t('PIM.TheArticlesYouOfferYourCustomers'),
    buttonText: t('PIM.NewArticle'),
  },
  categories: {
    title: t('PIM.Category'),
    description: t('PIM.CategoryDescription'),
    buttonText: t('PIM.NewCategory'),
  },
  menus: {
    title: t('PIM.Menu'),
    description: t('PIM.MenuDescription'),
    buttonText: t('PIM.NewMenu'),
  },
  ingredients: {
    title: t('PIM.Ingredient'),
    description: t('PIM.IngredientDescription'),
    buttonText: t('PIM.NewIngredient'),
  },
  ingredientGroup: {
    title: t('PIM.IngredientGroup'),
    description: t('PIM.GroupDescription'),
    buttonText: t('PIM.NewGroup'),
  },
  upsell: {
    title: t('PIM.UpsellGroup'),
    description: t('PIM.UpsellDescription'),
    buttonText: t('PIM.NewUpsell'),
  },
  courses: {
    title: t('PIM.Course'),
    description: t('PIM.CourseDescription'),
    buttonText: t('PIM.NewCourse'),
  },
  messages: {
    title: t('PIM.Message'),
    description: t('PIM.MessageDescription'),
    buttonText: t('PIM.NewMessage'),
  },
});

export const sortByOrderNumber = (a, b) => {
  if (a == null && b != null) {
    return 1;
  } else if (a != null && b == null) {
    return -1;
  }

  return b - a;
};

export const getIngredientsList = (variations) => {
  return variations?.filter((vari) => {
    if (vari.allergen) {
      return;
    }

    return vari;
  });
};

export const getCategoriesList = (categories, idToUpdate?) => {
  const { defaultLanguage } = useI18n();

  return (
    categories
      ?.filter((cat) => cat.id !== idToUpdate && cat.parentId !== idToUpdate)
      .map((cat) => ({
        id: cat.id,
        name: getLangText(cat.nameLang, defaultLanguage) ?? cat.name,
        vatRate: cat.vatRate,
        courseType: cat.courseType,
        order: cat.order,
      }))
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      }) ?? []
  );
};

export const getCoursetypeList = (coursetypes) => {
  return coursetypes
    .sort((a, b) => sortByOrderNumber(a.order, b.order))
    ?.map((type) => ({
      id: type.id,
      name: type.name ?? type.label,
    }));
};

export const sortItems = (list) => {
  const { defaultLanguage } = useI18n();

  if (!list) return [];
  return list.sort((a, b) =>
    getLangText(a.titleLang, defaultLanguage) < getLangText(b.titleLang, defaultLanguage) ? -1 : 1,
  );
};

export type ActivatedFeatures = {
  hasPOS: boolean;
  hasTables: boolean;
  hasAppOrders: boolean;
  hasKDS: boolean;
  hasExpress: boolean;
  hasInventory: boolean;
  manualEndOfDay: boolean;
};

export const getActivatedFeatures = (companyData) => {
  return {
    hasPOS: companyData?.hasPOS ?? false,
    hasTables: companyData?.hasTables ?? false,
    hasAppOrders: companyData?.hasAppOrders ?? false,
    hasKDS: companyData?.hasKDS ?? false,
    hasExpress: companyData?.hasExpress ?? false,
    hasInventory: companyData?.hasInventory ?? false,
    manualEndOfDay: companyData?.manualEndOfDay ?? false,
  };
};

export const getAllArticlesMenu = (companyData) => {
  const foundMenu = companyData?.menus?.find(
    (menu) => menu.name === 'All articles' && menu.deleted !== true,
  );
  return foundMenu;
};

export const getMenusList = (companyData) => {
  const allArticlesMenu = getAllArticlesMenu(companyData);

  return (
    companyData?.menus
      ?.filter((menu) => !menu.deleted && menu.id !== allArticlesMenu?.id)
      ?.map((menu) => ({
        id: menu.id,
        name: menu.name,
      }))
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      }) ?? []
  );
};

export const parseMenuEntry = (item) => {
  return {
    price: Math.round(item.price ?? item.defaultPrice ?? 0),
    item: {
      connect: { id: item.id },
    },
    selfService: false,
    instant: false,
  };
};

export const areFormValuesEqual = (formState, formChanges) => {
  let equal = true;
  for (const field of formState) {
    let emptyFieldValue = false;
    const fieldValue = field.value;
    if (
      !Number.isInteger(fieldValue) &&
      (!fieldValue ||
        JSON.stringify(fieldValue) === '[]' ||
        JSON.stringify(fieldValue) === '{}' ||
        JSON.stringify(fieldValue) === '{"id":""}')
    ) {
      emptyFieldValue = true;
    }

    let emptyChangedValue = false;
    const changedValue = formChanges[field.id]?.value;
    if (
      !Number.isInteger(fieldValue) &&
      (!changedValue ||
        JSON.stringify(changedValue) === '[]' ||
        JSON.stringify(changedValue) === '{}' ||
        JSON.stringify(changedValue) === '{"id":""}')
    ) {
      emptyChangedValue = true;
    }

    if (emptyFieldValue && emptyChangedValue) {
      continue;
    }

    if ((emptyFieldValue && !emptyChangedValue) || (!emptyFieldValue && emptyChangedValue)) {
      equal = false;
      break;
    }

    if (typeof fieldValue !== 'object' && typeof changedValue !== 'object') {
      if (fieldValue != changedValue) {
        equal = false;
        break;
      }
      continue;
    }

    if (Array.isArray(fieldValue) && Array.isArray(changedValue)) {
      if (fieldValue.length !== changedValue.length) {
        equal = false;
        break;
      }

      if (
        (fieldValue.length > 0 && typeof fieldValue[0] === 'string') ||
        (changedValue.length > 0 && typeof changedValue[0] === 'string')
      ) {
        const areEqualStringArrays = fieldValue.every((sel) => changedValue.includes(sel));

        if (!areEqualStringArrays) {
          equal = false;
          break;
        }
      }

      if (!fieldValue.every((value) => changedValue.find((chValue) => value.id === chValue.id))) {
        equal = false;
        break;
      }
      continue;
    }

    if (JSON.stringify(fieldValue) != JSON.stringify(changedValue)) {
      equal = false;
      break;
    }
  }

  return equal;
};
